import { parseHistoryChainId } from '@/components/History/utils';

import { isTriggerOrderType } from '@/constants/advancedOrder';

import { getTradeType } from '@/utils/0x/history/status';
import { MatchaTradeHistoryEntry } from '@/utils/0x/history/types';
import { MATCHA_LIMIT_ORDER } from '@/utils/liquidity-sources';
import { routes } from '@/utils/routes';

import {
  AmountCell,
  type BuyTokenSelection,
  NetworkCell,
  PriceCell,
  RelativeTime,
  ROW_CLASSES,
  TypeCell,
} from './cells';

export const TradeRow = ({
  entry,
  buySelection,
}: {
  entry: MatchaTradeHistoryEntry;
  buySelection?: BuyTokenSelection;
}) => {
  const isLimit = entry.liquiditySource === MATCHA_LIMIT_ORDER;
  const originChainId = parseHistoryChainId(entry.chainId);
  const destinationChainId = entry.destinationChainId
    ? parseHistoryChainId(entry.destinationChainId)
    : undefined;

  // The buy leg settles on the destination chain of a bridge, the origin chain otherwise.
  const buyChainId = destinationChainId ?? originChainId;
  const sell = {
    chainId: originChainId,
    ...(isLimit
      ? { address: entry.makerToken, symbol: entry.makerTokenSymbol, amount: entry.makerAmount }
      : { address: entry.takerToken, symbol: entry.takerTokenSymbol, amount: entry.takerAmount }),
  };
  const buy = {
    chainId: buyChainId,
    ...(isLimit
      ? { address: entry.takerToken, symbol: entry.takerTokenSymbol, amount: entry.takerAmount }
      : { address: entry.makerToken, symbol: entry.makerTokenSymbol, amount: entry.makerAmount }),
  };

  // Definitive orders have no onchain hash until filled, so the tx-details
  // route takes the order ID plus the funder wallet that resolves it.
  const txHash = entry.orderId ?? entry.transactionHash;
  const detailsHref = txHash
    ? {
        pathname: routes.TX,
        query: {
          chainId: String(originChainId ?? entry.chainId),
          txHash,
          ...(entry.funderAddress ? { funderAddress: entry.funderAddress } : {}),
        },
      }
    : undefined;

  return (
    <div className={ROW_CLASSES}>
      <NetworkCell originChainId={originChainId} destinationChainId={destinationChainId} />
      <AmountCell {...sell} buySelection={buySelection} />
      <AmountCell
        {...buy}
        buySelection={buySelection}
        isApproximate={isTriggerOrderType(entry.advancedOrderType)}
      />
      <PriceCell sell={sell} buy={buy} volumeUSD={entry.volumeUSD} />
      <RelativeTime date={new Date(entry.timestamp)} />
      <TypeCell
        type={
          entry.advancedOrderType ??
          getTradeType({
            destinationChainId,
            liquiditySource: entry.liquiditySource,
            recipient: entry.recipient,
          })
        }
        detailsHref={detailsHref}
      />
    </div>
  );
};
